import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import IResetPassword from "../../core/contracts/IResetPassword";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import {
  CircularProgress,
  Card,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { useParams } from "react-router-dom";
import PasswordRules from "../../components/PasswordRules";
import WMBack from "../../assets/images/wm_back.png";
import WMLogoOneLiner from "../../assets/icons/WMLogoOneLiner";
import clsx from "clsx";
import { useHubXSubscribe } from "../../HubX";

export interface IResetPasswordPageProps {
  onSubmit: (this: void, form: IResetPassword) => void;
  onCancel: (this: void) => void;
  loading?: boolean;
}

interface State {
  password: string;
  showPassword: boolean;
  confirmPassword: string;
  showConfirmPassword: boolean;
  currentPassword: string;
  showCurrentPassword: boolean;
}

const useStyles = makeStyles((theme) => {
  const buttonAndLoadingSpacing = theme.spacing(3, 0, 2);

  return {
    main: {
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(1) },
      [theme.breakpoints.up("sm")]: { padding: theme.spacing(3) },
    },
    hiddenOverflow: {
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    divStyle: {
      width: "100%",
      height: "100vh",
      backgroundImage: `url(${WMBack})`,
      backgroundSize: "cover",
    },
    wmLogo: {
      [theme.breakpoints.down("sm")]: { width: "218px" },
      [theme.breakpoints.up("sm")]: { width: "418px" },
    },
    textField: {
      width: "100%",
      paddingBottom: 10,
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white },
      [theme.breakpoints.up("sm")]: { color: theme.palette.common.black },
    },
    textPrimary: {
      fontSize: 24,
      fontWeight: "bold",
      fontVariant: "h5",
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white },
      [theme.breakpoints.up("sm")]: { color: theme.palette.common.black },
    },
    textSecond: {
      fontSize: 12,
      [theme.breakpoints.down("sm")]: { color: theme.palette.common.white, opacity: 1 },
      [theme.breakpoints.up("sm")]: { color: theme.palette.common.black, opacity: 0.5 },
    },
    textColorCommon: {
      [theme.breakpoints.down("sm")]: {
        color: theme.palette.common.white,
      },
      [theme.breakpoints.up("sm")]: {
        color: theme.palette.common.black,
      },
    },
    textLabelCommon: {
      [theme.breakpoints.down("sm")]: {
        "color": theme.palette.common.white,
        "&.Mui-focused": {
          color: theme.palette.common.white,
        },
      },
      [theme.breakpoints.up("sm")]: {
        "color": theme.palette.common.black,
        "&.Mui-focused": {
          color: theme.palette.primary.main,
        },
      },
    },
    buttonCommom: {
      borderRadius: 0,
      color: theme.palette.common.white,
    },
    paper: {
      marginTop: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      [theme.breakpoints.down("sm")]: { backgroundColor: "transparent" },
      [theme.breakpoints.up("sm")]: { backgroundColor: theme.palette.common.white },
      boxShadow: "none",
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.primary.main,
    },
    info: {
      marginTop: "20px",
    },
    form: {
      width: "100%", // Fix IE 11 issue.
      marginTop: theme.spacing(1),
    },
    submit_enviar: {
      margin: buttonAndLoadingSpacing,
      width: "48%",
      marginLeft: "4%",
    },
    submit_cancelar: {
      margin: buttonAndLoadingSpacing,
      width: "48%",
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      padding: buttonAndLoadingSpacing,
    },
    section: {
      "& svg": {
        "& .fil0": {
          fill: theme.palette.common.white,
        },
      },
      "textAlign": "center",
    },
  };
});

const ResetPasswordPage = (props: IResetPasswordPageProps) => {
  const { token } = useParams();
  const [formValues, setFormValues] = useState<IResetPassword>({
    username: "",
    token: token,
    name: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [values, setValues] = useState<State>({
    password: "",
    showPassword: false,
    confirmPassword: "",
    showConfirmPassword: false,
    currentPassword: "",
    showCurrentPassword: false,
  });
  const [buttonEnable, setButtonEnable] = useState(false);
  const [textEnable, setTextEnable] = useState(true);
  const [isAlteration, setIsAlteration] = React.useState<boolean | null>(null);
  const [isNewAccess, setIsNewAccess] = React.useState<boolean | null>(null);
  useHubXSubscribe("is_alteration", setIsAlteration, true);
  useHubXSubscribe("is_new_access", setIsNewAccess, true);

  const { onSubmit, onCancel, loading } = props;

  const handleInputChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    setButtonEnable(false);

    if (
      name !== "currentPassword" &&
      !(value.length < 8) &&
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[`~!@#$%^&*()_|+\-=?;:'",.<>\\{\\}\\[\]\\\\/]/.test(value)
    ) {
      setTextEnable(false);
    } else {
      if (name !== "currentPassword") {
        setTextEnable(true);
      }
    }

    if (name === "confirmPassword") {
      setTextEnable(false);
    }

    if (isAlteration) {
      if (
        name === "confirmPassword" &&
        value === formValues.newPassword &&
        formValues.currentPassword !== ""
      ) {
        setButtonEnable(true);
      }

      if (
        name === "newPassword" &&
        value === formValues.confirmPassword &&
        formValues.currentPassword !== ""
      ) {
        setButtonEnable(true);
      }

      if (
        name === "currentPassword" &&
        value !== "" &&
        formValues.newPassword !== "" &&
        formValues.newPassword === formValues.confirmPassword
      ) {
        setButtonEnable(true);
      }
    } else if (isNewAccess) {
      if (
        name === "confirmPassword" &&
        value === formValues.newPassword &&
        formValues.name !== ""
      ) {
        setButtonEnable(true);
      }

      if (
        name === "newPassword" &&
        value === formValues.confirmPassword &&
        formValues.name !== ""
      ) {
        setButtonEnable(true);
      }

      if (
        name === "name" &&
        value !== "" &&
        formValues.newPassword !== "" &&
        formValues.newPassword === formValues.confirmPassword
      ) {
        setButtonEnable(true);
      }
    } else {
      if (formValues.newPassword === value || value === formValues.confirmPassword) {
        setButtonEnable(true);
      }
    }
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleClickShowConfirmPassword = () => {
    setValues({ ...values, showConfirmPassword: !values.showConfirmPassword });
  };

  const handleClickShowCurrentPassword = () => {
    setValues({ ...values, showCurrentPassword: !values.showCurrentPassword });
  };

  const doReset = () => {
    onSubmit(formValues);
  };

  const doCancel = () => {
    onCancel();
  };

  function checkLenght(): boolean {
    return !(formValues.newPassword.length < 8);
  }

  function checkUpper(): boolean {
    return /[A-Z]/.test(formValues.newPassword);
  }

  function checkLower(): boolean {
    return /[a-z]/.test(formValues.newPassword);
  }

  function checkNumber(): boolean {
    return /[0-9]/.test(formValues.newPassword);
  }

  function checkSpecial(): boolean {
    return /[`~!@#$%^&*()_|+\-=?;:'",.<>\\{\\}\\[\]\\\\/]/.test(formValues.newPassword);
  }

  function checkConfirmPassword(): boolean {
    return formValues.newPassword !== "" && formValues.newPassword === formValues.confirmPassword;
  }

  const classes = useStyles();
  return (
    <section className={clsx(classes.hiddenOverflow, classes.divStyle)}>
      <section className={classes.section}>
        <WMLogoOneLiner className={classes.wmLogo} />
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Card className={clsx(classes.paper, classes.main)}>
            <Typography component="h1" className={classes.textPrimary}>
              {isAlteration && "Altere sua Senha"}
              {isNewAccess && "Cadastre sua Senha"}
              {!isAlteration && !isNewAccess && "Redefinir sua Senha"}
            </Typography>
            <Box mt={2}>
              <PasswordRules
                checkLengh={checkLenght()}
                checkLower={checkLower()}
                checkUpper={checkUpper()}
                checkNumber={checkNumber()}
                checkSpecial={checkSpecial()}
                checkConfirmPassword={checkConfirmPassword()}
              />
            </Box>
            <form className={classes.form} noValidate>
              {isNewAccess && (
                <FormControl className={classes.textField}>
                  <InputLabel className={classes.textLabelCommon}>Nome</InputLabel>
                  <Input
                    autoComplete="off"
                    required
                    fullWidth
                    onChange={handleInputChange}
                    name="name"
                    id="name"
                    className={classes.textColorCommon}
                  />
                </FormControl>
              )}
              {isAlteration && (
                <FormControl className={classes.textField}>
                  <InputLabel
                    htmlFor="standard-adornment-password"
                    className={classes.textLabelCommon}
                  >
                    Senha Atual
                  </InputLabel>
                  <Input
                    autoComplete="off"
                    required
                    fullWidth
                    onChange={handleInputChange}
                    name="currentPassword"
                    id="currentPassword"
                    className={classes.textColorCommon}
                    type={values.showCurrentPassword ? "text" : "password"}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowCurrentPassword}
                        >
                          {values.showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              )}
              <FormControl className={classes.textField}>
                <InputLabel
                  htmlFor="standard-adornment-password"
                  className={classes.textLabelCommon}
                >
                  Nova Senha
                </InputLabel>
                <Input
                  autoComplete="off"
                  required
                  fullWidth
                  onChange={handleInputChange}
                  name="newPassword"
                  id="newPassword"
                  className={classes.textColorCommon}
                  type={values.showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                      >
                        {values.showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>

              <FormControl className={classes.textField}>
                <InputLabel
                  htmlFor="standard-adornment-password"
                  className={classes.textLabelCommon}
                >
                  Repetir nova senha
                </InputLabel>
                <Input
                  autoComplete="off"
                  required
                  fullWidth
                  onChange={handleInputChange}
                  name="confirmPassword"
                  id="confirmPassword"
                  className={classes.textColorCommon}
                  type={values.showConfirmPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConfirmPassword}
                      >
                        {values.showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                  disabled={textEnable}
                />
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                className={clsx(classes.submit_cancelar, classes.buttonCommom)}
                onClick={doCancel}
              >
                Cancelar
              </Button>
              {!loading ? (
                <Button
                  variant="contained"
                  color="primary"
                  className={clsx(classes.submit_enviar, classes.buttonCommom)}
                  onClick={doReset}
                  disabled={!buttonEnable}
                >
                  Enviar
                </Button>
              ) : (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              )}
            </form>
          </Card>
        </Container>
      </section>
    </section>
  );
};

export default ResetPasswordPage;
